/**
 * History slice — snapshot-based undo/redo.
 *
 * Maintains past[] and future[] stacks of full node-array snapshots.
 * Each mutating action calls pushHistory() before executing.
 * Capped at 50 entries to bound memory usage.
 *
 * WQ-340: undo/redo persist. The target snapshot is applied to the server as a
 * diff (dataService.applySnapshot) and the stacks move only after that succeeds;
 * the store then reloads server truth. On failure the stacks are left as they
 * were, the map reloads from the server and the save-failed banner shows.
 * Undo/redo run in the serial write queue, so Ctrl+Z pressed while a save is in
 * flight waits for it, and repeated presses undo one step each.
 */

import type { StateCreator } from 'zustand';
import type { AdjacencyNode } from '../types/adjacency';
import { enqueue } from './persist-queue';
import type { AdjacencyStoreState } from './store-types';

const MAX_HISTORY = 50;

const snapshotOf = (nodes: AdjacencyNode[]) =>
  nodes.map(n => ({ ...n, edges: [...n.edges], parentIds: [...n.parentIds] }));

export interface HistorySlice {
  _past: AdjacencyNode[][];
  _future: AdjacencyNode[][];

  /** Snapshot current nodes onto the past stack. Call before any mutation. */
  pushHistory: () => void;

  /** Restore the most recent past snapshot. Pushes current state to future. */
  undo: () => Promise<void>;

  /** Restore the most recent future snapshot. Pushes current state to past. */
  redo: () => Promise<void>;
}

export const createHistorySlice: StateCreator<
  AdjacencyStoreState,
  [],
  [],
  HistorySlice
> = (set, get) => ({
  _past: [],
  _future: [],

  pushHistory: () => {
    const { nodes, _past } = get();
    const newPast = [..._past, snapshotOf(nodes)];
    if (newPast.length > MAX_HISTORY) newPast.shift();
    set({ _past: newPast, _future: [] });
  },

  undo: () => enqueue(async () => {
    const { _past, _future, nodes, dataService } = get();
    if (_past.length === 0) return;
    try {
      await dataService.applySnapshot(_past[_past.length - 1]);
      const newFuture = [..._future, snapshotOf(nodes)];
      if (newFuture.length > MAX_HISTORY) newFuture.shift();
      set({ _past: _past.slice(0, -1), _future: newFuture });
      await get().refreshNodes();
    } catch (e) {
      await get().handlePersistError(e);
    }
  }),

  redo: () => enqueue(async () => {
    const { _past, _future, nodes, dataService } = get();
    if (_future.length === 0) return;
    try {
      await dataService.applySnapshot(_future[_future.length - 1]);
      const newPast = [..._past, snapshotOf(nodes)];
      if (newPast.length > MAX_HISTORY) newPast.shift();
      set({ _past: newPast, _future: _future.slice(0, -1) });
      await get().refreshNodes();
    } catch (e) {
      await get().handlePersistError(e);
    }
  }),
});
